import {
  type CellContext,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type Location,
  useCreateLocationMutation,
  useGetLocations,
  useUpdateLocationMutation,
} from '~entities/locations';
import { useDeleteLocationMutation } from '~entities/locations/api/delete-location';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<Location>();

const columns = [
  columnHelper.accessor('fieldName', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Location Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => <div className='px-4 capitalize'>{info.getValue()}</div>,
  }),

  columnHelper.display({
    id: 'Action',
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Action'
        className='justify-end px-4 text-black'
      />
    ),
    cell: (info) => {
      return <LocationActionCell info={info} />;
    },
  }),
];

export function LocationListPage() {
  const [query, setQuery] = useState<string>('');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetLocations({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    ...(query ? { search: query } : {}),
  });

  const tableData = data?.items ?? [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const createMutation = useCreateLocationMutation();

  const handleCreateLocation = async () => {
    try {
      await createMutation.mutateAsync({
        fieldName: newLocationName,
      });
      setIsAddDialogOpen(false);
      setNewLocationName('');
      toast.success('Location created successfully');
    } catch (error) {
      toast.error('Failed to create location');
    }
  };

  return (
    <div className='w-full min-w-0 md:w-1/2 lg:w-1/3'>
      <EnrollioTable
        ariaLabel='Locations'
        totalLabel='Locations'
        data={tableData}
        columns={columns}
        rowCount={data?.pagination.total}
        isUpdating={isFetching}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
        filterHeader={() => {
          return (
            <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <Input
                placeholder='Search Locations'
                value={query}
                className='w-full sm:w-3/12'
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant='secondary'
                    size='sm'
                    className='w-full sm:w-auto'
                  >
                    <PlusIcon />
                    Add New
                  </Button>
                </DialogTrigger>
                <DialogContent className='sm:max-w-[425px]'>
                  <DialogHeader>
                    <DialogTitle>Add New Location</DialogTitle>
                    <DialogDescription>
                      Create a new location by entering the name below.
                    </DialogDescription>
                  </DialogHeader>
                  <div className='grid gap-4 py-4'>
                    <div className='grid grid-cols-4 items-center gap-4'>
                      <Label htmlFor='name' className='text-right'>
                        Location Name
                      </Label>
                      <Input
                        id='name'
                        value={newLocationName}
                        onChange={(event) => {
                          setNewLocationName(event.target.value);
                        }}
                        className='col-span-3'
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant={'outline'}
                      onClick={() => {
                        setIsAddDialogOpen(false);
                        setNewLocationName('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      onClick={handleCreateLocation}
                      disabled={
                        createMutation.isPending || !newLocationName.trim()
                      }
                    >
                      {createMutation.isPending ? (
                        <Loader2Icon className='animate-spin' />
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        }}
      />
    </div>
  );
}

function LocationActionCell({
  info,
}: {
  info: CellContext<Location, unknown>;
}) {
  const mutation = useUpdateLocationMutation();
  const deleteMutation = useDeleteLocationMutation();

  const initialLocationName = info.row.original.fieldName;
  const locationId = info.row.original._id;

  const [locationName, setLocationName] = useState(initialLocationName);
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({
        fieldId: locationId,
        fieldName: locationName,
      });
      setIsOpen(false);
      toast.success('Location updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update location');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({
        fieldId: locationId,
      });
      toast.success('Location deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete location');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <div className='flex items-center justify-end gap-2 px-4'>
          <TooltipProvider>
            <Tooltip>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <Button className='size-6 p-0 text-secondary [&_svg]:size-3'>
                    <PencilIcon />
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent>
                <p className='text-secondary'>Edit Location</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'destructive'}
                  className='size-6 p-0 [&_svg]:size-3'
                  onClick={handleDelete}
                >
                  <Trash2Icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='bg-destructive'>
                <p className='text-destructive-foreground'>Delete Location</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
            <DialogDescription>
              Make changes to your Location here. Click save when you&apos;re
              done.
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='name' className='text-right'>
                Location Name
              </Label>
              <Input
                id='name'
                value={locationName}
                onChange={(event) => {
                  setLocationName(event.target.value);
                }}
                className='col-span-3'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant={'outline'}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
